import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Easing,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import IconEmptyView from "../../../Components/IconEmptyView";
import { useDevice } from "../../../_layout";
import { useModalKeyboardShortcuts } from "../../../platform/guards/modalKeyboardShortcuts";
import {
  getNextNutritionPlanSelectionIndex,
  shouldRevealNutritionSelectionRow,
} from "./clientNutritionPlanImportModalUtils";

export const clientNutritionSelectionModalStyles = StyleSheet.create({
  modalRoot: {
    flex: 1,
    justifyContent: "center",
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(15, 23, 42, 0.1)",
    backdropFilter: "blur(1px)",
  },
  sheet: {
    alignSelf: "center",
    backgroundColor: Colors.whiteColor,
    borderColor: "rgba(15, 23, 42, 0.08)",
    borderRadius: 24,
    borderWidth: 1,
    boxShadow: "0 26px 90px rgba(15, 23, 42, 0.14)",
    overflow: "hidden",
    width: "100%",
  },
  searchHeader: {
    borderBottomColor: "rgba(15, 23, 42, 0.06)",
    borderBottomWidth: 1,
    gap: 14,
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: 14,
  },
  searchHeaderTopRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 12,
    justifyContent: "space-between",
  },
  searchHeaderEyebrow: {
    color: Colors.grayColor,
    fontSize: 11,
    fontWeight: "600",
    letterSpacing: 0.6,
    lineHeight: 14,
    textTransform: "uppercase",
  },
  selectionBadge: {
    alignItems: "center",
    flexDirection: "row",
    flexShrink: 1,
    gap: 8,
    minHeight: 20,
  },
  selectionBadgeText: {
    color: Colors.grayColor,
    flexShrink: 1,
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 16,
  },
  searchInputShell: {
    alignItems: "center",
    backgroundColor: Colors.whiteColor,
    borderColor: "rgba(15, 23, 42, 0.12)",
    borderRadius: 14,
    borderWidth: 1,
    flexDirection: "row",
    gap: 10,
    minHeight: 46,
    paddingHorizontal: 14,
  },
  searchInputShellInteractive: {
    borderColor: Colors.grayColor,
  },
  searchInput: {
    color: Colors.blackColor,
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    outlineStyle: "none",
    paddingVertical: 0,
  },
  searchInputIOS: {
    fontSize: 16,
  },
  closeButton: {
    alignItems: "center",
    backgroundColor: Colors.whiteColor,
    borderRadius: 999,
    height: 30,
    justifyContent: "center",
    width: 30,
  },
  body: {
    flex: 1,
    minHeight: 0,
    position: "relative",
  },
  scrollView: {
    flex: 1,
    minHeight: 0,
    overflowY: "scroll",
    scrollbarColor: "rgba(15, 23, 42, 0.42) rgba(15, 23, 42, 0.06)",
    scrollbarGutter: "stable",
    scrollbarWidth: "auto",
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 10,
    paddingTop: 10,
    paddingBottom: 12,
  },
  resultRowTouchable: {
    borderRadius: 16,
  },
  resultRow: {
    alignItems: "center",
    borderColor: "transparent",
    borderRadius: 16,
    borderWidth: 1,
    flexDirection: "row",
    gap: 14,
    justifyContent: "space-between",
    minHeight: 54,
    paddingHorizontal: 16,
    paddingVertical: 10,
    transitionDuration: "220ms",
    transitionProperty: "background-color, border-color",
    transitionTimingFunction: "ease-out",
  },
  resultRowSelected: {
    backgroundColor: "rgba(63, 123, 141, 0.06)",
    borderRadius: 14,
    borderColor: "rgba(63, 123, 141, 0.42)",
  },
  resultRowHover: {
    backgroundColor: "#f8fafb",
  },
  resultMain: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: 10,
    minWidth: 0,
  },
  resultIconWrap: {
    alignItems: "center",
    justifyContent: "center",
    width: 18,
  },
  resultName: {
    color: Colors.blackColor,
    flexShrink: 1,
    fontSize: 13,
    fontWeight: "500",
    lineHeight: 18,
  },
  resultKcalText: {
    color: Colors.grayColor,
    flexShrink: 0,
    fontSize: 11,
    fontWeight: "500",
    lineHeight: 16,
  },
  resultUpdatedText: {
    color: Colors.grayColor,
    fontSize: 11,
    fontStyle: "italic",
    lineHeight: 16,
    marginLeft: 14,
  },
  loadingState: {
    alignItems: "center",
    gap: 10,
    justifyContent: "center",
    minHeight: 220,
    paddingHorizontal: 24,
    paddingVertical: 36,
  },
  loadingStateTitle: {
    color: Colors.blackColor,
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
    textAlign: "center",
  },
  footer: {
    borderTopColor: "rgba(15, 23, 42, 0.06)",
    borderTopWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  footerText: {
    color: Colors.grayColor,
    fontSize: 10,
    lineHeight: 14,
    textAlign: "center",
  },
});

export default function ClientNutritionSelectionModal({
  badgeIconName = "person-outline",
  badgeText = "",
  buildSearchResults,
  emptyState,
  eyebrowText = "",
  isLoading = false,
  isVisible = false,
  items = [],
  loadingTitle = "",
  onClose,
  onSelectItem,
  renderItemRow,
  searchPlaceholder = "",
  searchAccessibilityLabel = searchPlaceholder,
}) {
  const { t, i18n } = useTranslation();
  const { isMobile, isIOS } = useDevice();
  const { height: windowHeight } = useWindowDimensions();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const inputRef = useRef(null);
  const sheetRef = useRef(null);
  const scrollViewRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const selectionInputSourceRef = useRef(null);
  const openProgress = useRef(new Animated.Value(0)).current;
  const listLayoutsRef = useRef({});
  const listViewportHeightRef = useRef(0);
  const scrollOffsetYRef = useRef(0);
  const [isMounted, setIsMounted] = useState(isVisible);
  const [searchText, setSearchText] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sourceItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const resultItems = useMemo(() => {
    if (typeof buildSearchResults !== "function") {
      return sourceItems;
    }

    return buildSearchResults(sourceItems, searchText, language);
  }, [buildSearchResults, language, searchText, sourceItems]);
  const hasResults = resultItems.length > 0;
  const selectedItem =
    selectedIndex >= 0 && selectedIndex < resultItems.length
      ? resultItems[selectedIndex]
      : null;
  const sheetHeight = Math.min(windowHeight - 56, isMobile ? 560 : 620);

  const closeModal = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    onClose?.();
  }, [onClose]);

  const ensureSelectedRowVisible = useCallback((index) => {
    const layout = listLayoutsRef.current[index];
    const viewportHeight = listViewportHeightRef.current;
    const currentScrollOffset = scrollOffsetYRef.current;

    if (
      !layout ||
      viewportHeight <= 0 ||
      typeof scrollViewRef.current?.scrollTo !== "function"
    ) {
      return;
    }

    const visibleTop = currentScrollOffset;
    const visibleBottom = visibleTop + viewportHeight;

    if (layout.y < visibleTop + 6) {
      scrollViewRef.current.scrollTo({
        y: Math.max(layout.y - 6, 0),
        animated: true,
      });
      return;
    }

    if (layout.y + layout.height > visibleBottom - 6) {
      scrollViewRef.current.scrollTo({
        y: Math.max(layout.y + layout.height - viewportHeight + 6, 0),
        animated: true,
      });
    }
  }, []);

  const handleSelectItem = useCallback(
    async (itemOverride = null) => {
      if (isSubmittingRef.current) {
        return;
      }

      const nextItem = itemOverride ?? selectedItem;
      if (!nextItem) {
        return;
      }

      isSubmittingRef.current = true;
      setIsSubmitting(true);

      try {
        await onSelectItem?.(nextItem);
        onClose?.();
      } catch (error) {
        return;
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [onClose, onSelectItem, selectedItem],
  );

  useModalKeyboardShortcuts({
    isVisible: isMounted,
    onConfirm: handleSelectItem,
    onCancel: closeModal,
    isConfirmEnabled: hasResults && !isSubmitting,
    allowEnterOnSingleLineInput: true,
    isBlocked: isSubmitting,
  });

  useEffect(() => {
    openProgress.stopAnimation();

    if (isVisible) {
      setIsMounted(true);
      setSearchText("");
      setHoveredIndex(-1);
      selectionInputSourceRef.current = null;
      isSubmittingRef.current = false;
      setIsSubmitting(false);

      requestAnimationFrame(() => {
        Animated.timing(openProgress, {
          toValue: 1,
          duration: 220,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }).start();
      });

      return undefined;
    }

    if (!isMounted) {
      return undefined;
    }

    Animated.timing(openProgress, {
      toValue: 0,
      duration: 160,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        setIsMounted(false);
      }
    });

    return undefined;
  }, [isMounted, isVisible, openProgress]);

  useEffect(() => {
    if (!isMounted || isMobile) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      inputRef.current?.focus?.();
    }, 70);

    return () => clearTimeout(timeoutId);
  }, [isMobile, isMounted]);

  useEffect(() => {
    setSelectedIndex(resultItems.length > 0 ? 0 : -1);
    setHoveredIndex(-1);
    selectionInputSourceRef.current = null;
    listLayoutsRef.current = {};
    scrollOffsetYRef.current = 0;
    scrollViewRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [resultItems.length, searchText]);

  useEffect(() => {
    if (
      shouldRevealNutritionSelectionRow(
        selectedIndex,
        selectionInputSourceRef.current,
      )
    ) {
      ensureSelectedRowVisible(selectedIndex);
    }
  }, [ensureSelectedRowVisible, selectedIndex]);

  useEffect(() => {
    if (!isMounted || typeof window === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event?.key !== "ArrowDown" && event?.key !== "ArrowUp") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      selectionInputSourceRef.current = "keyboard";
      setSelectedIndex((currentIndex) => {
        const nextIndex = getNextNutritionPlanSelectionIndex(
          currentIndex,
          event.key === "ArrowUp" ? "up" : "down",
          resultItems.length,
        );
        setHoveredIndex(nextIndex);
        return nextIndex;
      });
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isMounted, resultItems.length]);

  useEffect(() => {
    if (!isMounted || typeof document === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event?.key !== "Tab") {
        return;
      }

      const focusableElements = Array.from(
        sheetRef.current?.querySelectorAll?.(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => {
        return (
          typeof element?.focus === "function" &&
          element?.disabled !== true &&
          element?.getAttribute?.("aria-hidden") !== "true"
        );
      });

      if (focusableElements.length === 0) {
        return;
      }

      const activeElement = document.activeElement;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!sheetRef.current?.contains?.(activeElement)) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isMounted]);

  if (!isMounted) {
    return null;
  }

  const backdropOpacity = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });
  const sheetOpacity = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.72, 1],
  });
  const sheetTranslateY = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [14, 0],
  });
  const sheetScale = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.985, 1],
  });
  const resolvedEmptyState =
    typeof emptyState === "function"
      ? emptyState({
          items: sourceItems,
          resultItems,
          searchText,
          t,
        })
      : emptyState;

  return (
    <Modal
      onRequestClose={closeModal}
      transparent
      visible={isMounted}
      animationType="none"
    >
      <View style={clientNutritionSelectionModalStyles.modalRoot}>
        <Animated.View
          style={[
            clientNutritionSelectionModalStyles.backdrop,
            { opacity: backdropOpacity },
          ]}
        />
        <Pressable onPress={closeModal} style={StyleSheet.absoluteFillObject} />

        <Animated.View
          ref={sheetRef}
          style={[
            clientNutritionSelectionModalStyles.sheet,
            {
              height: sheetHeight,
              maxWidth: isMobile ? 620 : 760,
              opacity: sheetOpacity,
              transform: [{ translateY: sheetTranslateY }, { scale: sheetScale }],
            },
          ]}
        >
          <View style={clientNutritionSelectionModalStyles.searchHeader}>
            <View style={clientNutritionSelectionModalStyles.searchHeaderTopRow}>
              <Text style={clientNutritionSelectionModalStyles.searchHeaderEyebrow}>
                {eyebrowText}
              </Text>
              {badgeText ? (
                <View style={clientNutritionSelectionModalStyles.selectionBadge}>
                  <Ionicons
                    name={badgeIconName}
                    size={13}
                    color={Colors.grayColor}
                  />
                  <Text
                    numberOfLines={1}
                    style={clientNutritionSelectionModalStyles.selectionBadgeText}
                  >
                    {badgeText}
                  </Text>
                </View>
              ) : null}
            </View>

            <View
              onMouseEnter={!isMobile ? () => setIsSearchHovered(true) : undefined}
              onMouseLeave={!isMobile ? () => setIsSearchHovered(false) : undefined}
              style={[
                clientNutritionSelectionModalStyles.searchInputShell,
                isInputFocused || isSearchHovered
                  ? clientNutritionSelectionModalStyles.searchInputShellInteractive
                  : null,
              ]}
            >
              <Ionicons name="search-outline" size={18} color={Colors.grayColor} />
              <TextInput
                ref={inputRef}
                accessibilityLabel={searchAccessibilityLabel}
                aria-label={searchAccessibilityLabel}
                autoCapitalize="none"
                autoCorrect={false}
                editable={!isSubmitting}
                onBlur={() => setIsInputFocused(false)}
                onChangeText={setSearchText}
                onFocus={() => setIsInputFocused(true)}
                placeholder={searchPlaceholder}
                placeholderTextColor={Colors.grayColor}
                style={[
                  clientNutritionSelectionModalStyles.searchInput,
                  isMobile && isIOS
                    ? clientNutritionSelectionModalStyles.searchInputIOS
                    : null,
                ]}
                value={searchText}
              />
              <TouchableOpacity
                activeOpacity={0.8}
                disabled={isSubmitting}
                onMouseEnter={!isMobile ? () => setIsCloseHovered(true) : undefined}
                onMouseLeave={() => setIsCloseHovered(false)}
                onPress={closeModal}
                style={clientNutritionSelectionModalStyles.closeButton}
              >
                {isSubmitting ? (
                  <ActivityIndicator color={Colors.grayColor} size="small" />
                ) : (
                  <Ionicons
                    name="close"
                    size={20}
                    color={isCloseHovered ? Colors.darkgrayColor : Colors.grayColor}
                  />
                )}
              </TouchableOpacity>
            </View>
          </View>

          <View style={clientNutritionSelectionModalStyles.body}>
            <ScrollView
              ref={scrollViewRef}
              keyboardShouldPersistTaps="handled"
              onLayout={(event) => {
                listViewportHeightRef.current = event?.nativeEvent?.layout?.height ?? 0;
              }}
              onScroll={(event) => {
                scrollOffsetYRef.current = event?.nativeEvent?.contentOffset?.y ?? 0;
              }}
              scrollEventThrottle={16}
              showsVerticalScrollIndicator
              style={clientNutritionSelectionModalStyles.scrollView}
              contentContainerStyle={clientNutritionSelectionModalStyles.scrollContent}
            >
              {hasResults
                ? resultItems.map((item, index) =>
                    renderItemRow?.({
                      item,
                      index,
                      isHovered: hoveredIndex === index,
                      isMobile,
                      isSelected: selectedIndex === index,
                      language,
                      onHover:
                        isSubmitting
                          ? undefined
                          : () => {
                              selectionInputSourceRef.current = "pointer";
                              setHoveredIndex(index);
                              setSelectedIndex(index);
                            },
                      onLayout: (event) => {
                        listLayoutsRef.current[index] = {
                          height: event?.nativeEvent?.layout?.height ?? 0,
                          y: event?.nativeEvent?.layout?.y ?? 0,
                        };
                      },
                      onPress: () => {
                        selectionInputSourceRef.current = "pointer";
                        setSelectedIndex(index);
                        void handleSelectItem(item);
                      },
                      styles: clientNutritionSelectionModalStyles,
                      t,
                    })
                  )
                : null}

              {!hasResults ? (
                <View style={clientNutritionSelectionModalStyles.loadingState}>
                  {isLoading ? (
                    <>
                      <ActivityIndicator color={Colors.bubbleColor} size="small" />
                      <Text style={clientNutritionSelectionModalStyles.loadingStateTitle}>
                        {loadingTitle}
                      </Text>
                    </>
                  ) : (
                    <IconEmptyView
                      iconLibrary={resolvedEmptyState?.iconLibrary ?? "Ionicons"}
                      iconName={resolvedEmptyState?.iconName ?? "search-outline"}
                      marginTop={32}
                      marginBottom={32}
                      mainText={resolvedEmptyState?.mainText}
                      subText={resolvedEmptyState?.subText}
                    />
                  )}
                </View>
              ) : null}
            </ScrollView>
          </View>

          {!isMobile ? (
            <View style={clientNutritionSelectionModalStyles.footer}>
              <Text style={clientNutritionSelectionModalStyles.footerText}>
                ↑↓ {t("navigate-string")}
                {"    "}↵ {t("select-string")}
                {"    "}Esc {t("close-string")}
              </Text>
            </View>
          ) : null}
        </Animated.View>
      </View>
    </Modal>
  );
}
